import { tradutorDe, type Tradutor } from '../i18n/tela.js';
/**
 * O que uma prateleira pode conter — curso, módulo, aula ou TRILHA — e quem
 * enxerga o quê.
 *
 * ## Por que os três, e por que a prateleira pode misturar
 *
 * A prateleira nasceu como faixa de cursos porque a vitrine tinha um objeto só.
 * Ela deixou de ter: um módulo é uma unidade que o dono nomeia, ordena e agora
 * capa; uma aula é a coisa que a pessoa efetivamente abre. "Comece por aqui" com
 * o curso de boas-vindas, o módulo de configuração da conta e a aula de
 * apresentação é a faixa que o dono desenha primeiro, e hoje ela é impossível.
 *
 * **Faixa mista é permitida, e a decisão é deliberada.** Proibir custaria uma
 * trava por prateleira (`kind` de item fixado na faixa), e compraria só
 * uniformidade visual — que não é o problema, porque os três cartões já não são
 * iguais: cada um imprime a sua própria legenda (ver `legendaDoItem`). O que
 * proibir *quebraria* é o caso que motivou o pedido. Uma regra que impede o uso
 * principal para arrumar a aparência do caso raro está do lado errado.
 *
 * ## A regra de acesso olha para o CURSO em três dos quatro
 *
 * Um módulo e uma aula não têm grupo de acesso próprio — quem tem é o curso dono
 * deles, e é assim que `exigirCurso` já trata a sala de aula. Então a pergunta
 * que a prateleira faz para eles é sempre a mesma,
 * `podeVerItem(visiveis, item.cursoId)`, e é por isso que **os três carregam o
 * `cursoId` do dono**, inclusive o item do tipo `curso`, onde ele é o próprio id.
 *
 * **A trilha é a quarta, e ela pergunta por SI** (0467): desde a `0467` ela tem
 * grupo próprio (`access_group_track`), logo o veredito dela é
 * `podeVerItem(trilhasVisiveis, item.id)`. São **duas listas e uma régua** — a
 * mesma `podeVerItem`, a mesma `itensVisiveis` um andar acima, os mesmos grupos
 * e a mesma validade. Fazê-la perguntar pelo curso-sombra dela faria o cartaz
 * depender de uma linha que o tutor não vê, e o sombra existe para não ser visto.
 *
 * E por isso `cursoId` saiu de `Comum`: a trilha **não tem** curso dono, e um
 * campo obrigatório obrigaria quem a constrói a inventar um valor.
 *
 * Se a regra olhasse para `item.id`, um módulo nunca estaria em grupo nenhum e a
 * faixa mostraria a aula 3 de um curso que a pessoa não comprou. É a falha que
 * este arquivo existe para tornar impossível, e é por isso que ela mora aqui e
 * não num `where`: a vitrine, a prévia do Estúdio e o editor do painel fazem a
 * mesma pergunta, e a cópia que ficasse diferente seria a que vaza.
 *
 * ## Some, não vira cadeado
 *
 * O que a pessoa não alcança **não aparece**. Um cadeado na vitrine anuncia o
 * que ela não comprou dentro da área pela qual ela pagou, e o efeito de anunciar
 * é o chamado de suporte, não a venda.
 *
 * A exceção é a que o produto já tinha: curso com `paginaDeVenda` continua
 * aparecendo, porque aí existe para onde mandar quem clicar. Módulo e aula não
 * herdam essa exceção — o cartão diria `Aula 3 · 12 min` e o clique abriria um
 * checkout de curso, que é uma promessa trocada.
 *
 * **A TRILHA herda-a (0467), e módulo e aula continuam a não herdar.** A
 * diferença não é de tamanho: na trilha não há promessa trocada nenhuma — o
 * cartão diz «Trilha · 4 cursos» e o clique abre a venda DAQUELA trilha, que é
 * o objeto que o dono manda comercializar *«como os cursos são»*. Recusar-lhe a
 * exceção deixaria a trilha paga sem vitrine, e sem vitrine ela não se vende.
 *
 * A segunda exceção é `amostraGratis`, e ela não é nova: `lesson.free_preview`
 * existe exatamente para "a vitrine servir de página de venda sem uma segunda
 * tela". Uma aula marcada como amostra aparece e **abre de verdade** — não vira
 * venda —, porque abrir é o que a marca quer dizer.
 */

import { type ItensVisiveis, podeVerItem } from './acesso.js';

/**
 * O terceiro argumento para quem NÃO desenha trilha (0467).
 *
 * O herói e a estante do telemóvel montam itens de curso, módulo e aula um a
 * um — nunca uma trilha —, e para esses três o conjunto das trilhas não é
 * consultado. Passar isto é dizer «aqui não há trilha a alcançar», que é
 * verdade, em vez de passar `{modo:'tudo'}`, que diria «alcança todas» e seria
 * uma afirmação falsa à espera do dia em que alguém lhe mandasse uma trilha.
 */
export const NENHUMA_TRILHA: ItensVisiveis = { modo: 'alguns', ids: new Set() };

/**
 * As tres especies que PERTENCEM a um curso, e por isso herdam o acesso dele.
 *
 * Nomeadas a parte (0467) porque ha leitores que precisam exactamente destas
 * tres e NAO da trilha. Em 06/10 o leitor era `TIPOS_DE_SLIDE`; a 08/10 o heroi
 * aprendeu a trilha e deixou de o ser. Os que ficam sao os que consultam o
 * CURSO DONO — `ehSlideDeItem`, `enfeitarChaves`, `exigirCursosDaArea` —, e
 * para esses a lista estreita e a pergunta, nao uma restricao de produto.
 */
export const TIPOS_DE_ITEM_DE_CURSO = ['curso', 'modulo', 'aula'] as const;
export type TipoDeItemDeCurso = (typeof TIPOS_DE_ITEM_DE_CURSO)[number];

/** O que a prateleira aceita: as tres de cima, mais a TRILHA (0467). */
export const TIPOS_DE_ITEM = [...TIPOS_DE_ITEM_DE_CURSO, 'trilha'] as const;
export type TipoDeItemDePrateleira = (typeof TIPOS_DE_ITEM)[number];

interface Comum {
  /** O id do próprio item — é o que o cartão abre. */
  id: string;
  posicao: number;
}

/**
 * O que pertence a um curso, e por isso herda a pergunta de acesso dele.
 *
 * Três dos quatro tipos. `cursoId` saiu de `Comum` quando a trilha entrou
 * (0467), e a mudança é a que o compilador precisava: **a trilha não tem curso
 * dono**, e deixá-la herdar um `cursoId: string` obrigaria quem a construísse a
 * inventar um valor — na prática o curso-sombra, que é exactamente a linha que o
 * sombra existe para esconder.
 */
interface DeCurso extends Comum {
  /**
   * O curso DONO. Para `tipo: 'curso'` é o próprio `id`.
   *
   * Denormalizado também na tabela, e pelo mesmo motivo: sem ele, decidir a
   * visibilidade de um módulo exigiria um join na hora de desenhar a vitrine.
   */
  cursoId: string;
}

/**
 * Um item, com o que basta para decidir se ele aparece.
 *
 * União discriminada e não um objeto com tudo opcional: `aulasPublicadas` não
 * existe para uma aula e `amostraGratis` não existe para um módulo, e um objeto
 * largo faria o `if` errado passar no typecheck.
 */
export type ItemDePrateleira =
  | (DeCurso & {
      tipo: 'curso';
      publicado: boolean;
      /** `course.offer_url`. Nulo = 404 para quem não tem acesso, o padrão. */
      paginaDeVenda: string | null;
    })
  | (Comum & {
      /**
       * A TRILHA na prateleira (0467) — e é o único tipo sem curso dono.
       *
       * Ordem do dono de 06/10/2026: *«na vitrine ele aparece como um curso mas
       * possui marcação especial para mostrar que é trilha»*. Daí ela ter os
       * mesmos dois campos de estado do curso — e **nenhum** `cursoId`.
       */
      tipo: 'trilha';
      publicada: boolean;
      /**
       * `area_track.offer_url`. A trilha ganha a MESMA excepção do curso: sem
       * acesso e com endereço, ela aparece para vender.
       *
       * Era tentador fazê-la sumir como o módulo e a aula somem, e seria errado:
       * módulo e aula não herdam a excepção porque o cartão diria «Aula 3 · 12
       * min» e o clique abriria um checkout de CURSO — uma promessa trocada. Na
       * trilha não há troca nenhuma: o cartão diz «Trilha · 4 cursos» e o clique
       * abre a venda DAQUELA trilha. É o que *«comercializadas como os cursos
       * são»* significa, e recusá-lo deixaria a trilha paga sem vitrine.
       */
      paginaDeVenda: string | null;
      /**
       * Quantos cursos desta trilha ESTA pessoa alcança. Zero faz o item sumir,
       * pelo mesmo argumento de `'vazio'` no módulo: um cartão que promete um
       * percurso e abre uma lista em branco é o cartão que mente.
       *
       * Não se aplica a quem a vê como venda — ali ela ainda não é dela.
       */
      cursosVisiveis: number;
    })
  | (DeCurso & {
      tipo: 'modulo';
      /** `course_module.hidden`. */
      oculto: boolean;
      /** Quantas aulas publicadas ele tem, contando os submódulos. */
      aulasPublicadas: number;
      /** Do curso dono — para o cartão poder dizer de onde o módulo veio. */
      cursoPublicado: boolean;
    })
  | (DeCurso & {
      tipo: 'aula';
      publicada: boolean;
      /** `lesson.free_preview`. */
      amostraGratis: boolean;
      /** Segundos. Nulo em aula de texto, e o cartão não inventa um número. */
      duracaoSegundos: number | null;
      cursoPublicado: boolean;
    });

export type MotivoDeSumir =
  /** Rascunho — do próprio item, ou do curso dono. */
  | 'rascunho'
  /** Módulo escondido de propósito pelo dono. */
  | 'oculto'
  /**
   * Módulo sem nenhuma aula publicada: o cartão diria "0 aulas".
   *
   * Desde a 0467 também a TRILHA de que esta pessoa não alcança curso nenhum:
   * o cartaz prometeria um percurso e abriria uma lista em branco.
   */
  | 'vazio'
  /** Grupo de acesso: esta pessoa não alcança o curso dono. */
  | 'sem_acesso';

export interface VereditoDoItem {
  aparece: boolean;
  /** Verdadeiro só para curso sem acesso e com página de venda. */
  comoVenda: boolean;
  /**
   * Esta AULA aparece porque é **amostra grátis**, e não porque o curso é desta
   * pessoa (02/10/2026).
   *
   * ## Por que não reusei o `comoVenda`
   *
   * Porque ele já tem leitor, e o que ele diz lá é outra coisa:
   * `routes/app-estante.ts` manda-o ao telemóvel como **`trancado`**. A aula de
   * amostra é o contrário de trancada — desde `73a10fd2c` ela ABRE, por quatro
   * rotas de leitura. Reusar o campo poria um cadeado no app sobre a única coisa
   * da prateleira que o servidor acabou de destrancar.
   *
   * ## O que a tela faz com ele, e o que ela NÃO faz
   *
   * Desenha o carimbo «Amostra» sobre o cartaz. **Não** apaga a cor da arte e
   * **não** põe véu: esses são a linguagem de «fechado», e esta aula não está
   * fechada. O que o carimbo responde é outra pergunta — *por que é que esta
   * aula solta está aqui, se o curso não é meu?* —, e a resposta honesta é: é
   * uma amostra.
   *
   * Falso para quem TEM o curso. Lá a mesma aula é só mais uma aula, e um
   * carimbo seria ruído sobre conteúdo que já é dela.
   */
  porAmostra: boolean;
  motivo: MotivoDeSumir | null;
}

const APARECE: VereditoDoItem = {
  aparece: true,
  comoVenda: false,
  porAmostra: false,
  motivo: null,
};

function some(motivo: MotivoDeSumir): VereditoDoItem {
  return { aparece: false, comoVenda: false, porAmostra: false, motivo };
}

/**
 * Este item entra nesta prateleira, para esta pessoa?
 *
 * ## A ordem das perguntas importa
 *
 * O estado de publicação vem **antes** do acesso, e não depois. Um rascunho não
 * é de ninguém: mostrá-lo a quem tem acesso porque "essa pessoa poderia ver o
 * curso" é como o dono descobre que o módulo que ele estava montando apareceu na
 * vitrine da turma inteira.
 *
 * O acesso vem por último porque é a única pergunta cuja resposta depende de
 * quem está olhando — e é a única que tem exceção.
 */
export function itemApareceNaPrateleira(
  item: ItemDePrateleira,
  visiveis: ItensVisiveis,
  /**
   * As TRILHAS que esta pessoa alcança (0467).
   *
   * **Obrigatório, e sem valor padrão**, pela razão escrita em `cursosVisiveis`
   * para o `abertos`: um padrão faria o próximo chamador esquecido compilar e
   * voltar em silêncio — e aqui o silêncio seria uma trilha paga a aparecer
   * para quem não a tem, ou a sumir para quem a tem.
   */
  trilhasVisiveis: ItensVisiveis,
): VereditoDoItem {
  /* 1. O item existe para alguém? */
  switch (item.tipo) {
    case 'curso':
      if (!item.publicado) return some('rascunho');
      break;
    case 'trilha':
      if (!item.publicada) return some('rascunho');
      break;
    case 'modulo':
      if (!item.cursoPublicado) return some('rascunho');
      if (item.oculto) return some('oculto');
      /* Zero aulas publicadas é o cartão que mente: ele promete conteúdo e abre
       * uma lista vazia. Ver `montarPrateleiras`, que já filtra a faixa vazia
       * pelo mesmo motivo, um nível acima. */
      if (item.aulasPublicadas === 0) return some('vazio');
      break;
    case 'aula':
      if (!item.cursoPublicado) return some('rascunho');
      if (!item.publicada) return some('rascunho');
      break;
  }

  /*
   * 2. Esta pessoa alcança o objeto?
   *
   * A trilha pergunta por SI — `podeVerItem(trilhasVisiveis, item.id)` —, e os
   * outros três perguntam pelo curso dono. São duas listas e **uma régua**: a
   * mesma `podeVerItem`, a mesma `itensVisiveis` um andar acima, os mesmos
   * grupos e a mesma validade. Não há `podeVerTrilha`, e inventar um nome por
   * espécie é como se chega a duas réguas que divergem.
   */
  if (item.tipo === 'trilha') {
    if (podeVerItem(trilhasVisiveis, item.id)) {
      /* Alcança — mas uma trilha de que ela não vê curso nenhum é um cartaz
         que promete um percurso e abre uma lista vazia. Mesmo argumento do
         `'vazio'` do módulo, um `if` acima. */
      return item.cursosVisiveis > 0 ? APARECE : some('vazio');
    }
  } else if (podeVerItem(visiveis, item.cursoId)) {
    return APARECE;
  }

  /* 3. Sem acesso: as três exceções, e nada além delas. Ver o cabeçalho. */
  if (
    (item.tipo === 'curso' || item.tipo === 'trilha') &&
    item.paginaDeVenda !== null &&
    item.paginaDeVenda !== ''
  ) {
    return { aparece: true, comoVenda: true, porAmostra: false, motivo: null };
  }
  /* A aula de amostra — e a marca de que foi ELA que abriu esta porta. Ver
     `porAmostra`, acima, para por que não é o `comoVenda`. */
  if (item.tipo === 'aula' && item.amostraGratis) {
    return { aparece: true, comoVenda: false, porAmostra: true, motivo: null };
  }

  return some('sem_acesso');
}

export interface ItemNaVitrine {
  tipo: TipoDeItemDePrateleira;
  id: string;
  /** **Nulo na trilha** (0467): ela não pertence a curso nenhum. */
  cursoId: string | null;
  comoVenda: boolean;
  /** Esta aula entrou por ser amostra grátis. Ver `VereditoDoItem.porAmostra`. */
  porAmostra: boolean;
}

/**
 * A prateleira, já filtrada e na ordem gravada.
 *
 * Devolve `tipo` junto com `id` de propósito: a vitrine precisa dos dois para
 * saber qual cartão desenhar e para onde o clique vai, e um `string[]` de ids
 * — que é o que a faixa de cursos devolvia — deixou de bastar no momento em que
 * dois itens de tipos diferentes podem ter a mesma origem.
 */
export function itensDaPrateleira(
  itens: ItemDePrateleira[],
  visiveis: ItensVisiveis,
  trilhasVisiveis: ItensVisiveis,
): ItemNaVitrine[] {
  return itens
    .slice()
    .sort((a, b) => a.posicao - b.posicao || a.id.localeCompare(b.id))
    .map((i) => ({ item: i, veredito: itemApareceNaPrateleira(i, visiveis, trilhasVisiveis) }))
    .filter((x) => x.veredito.aparece)
    .map((x) => ({
      tipo: x.item.tipo,
      id: x.item.id,
      cursoId: x.item.tipo === 'trilha' ? null : x.item.cursoId,
      comoVenda: x.veredito.comoVenda,
      porAmostra: x.veredito.porAmostra,
    }));
}

/** O par que identifica um item dentro de uma faixa. */
export interface ChaveDoItem {
  tipo: TipoDeItemDePrateleira;
  id: string;
}

/**
 * A lista de itens depois de trocar SÓ os cursos.
 *
 * ## O defeito que isto existe para não deixar acontecer
 *
 * A ficha do curso, no Estúdio, tem um interruptor por prateleira manual
 * (`AncoraDePrateleira`). Ele lê a lista de cursos da faixa, acrescenta ou tira
 * um, e manda a lista inteira de volta — porque até a 0116 a faixa **era** uma
 * lista de cursos, e substituir tudo era exato.
 *
 * Deixou de ser. Numa faixa que também tem um módulo e uma aula, aquele mesmo
 * interruptor mandaria um array só de cursos, e a substituição literal apagaria
 * os outros dois. O dono ligaria um curso em "Comece por aqui" e perderia o
 * módulo que tinha posto ali na semana passada, sem erro nenhum na tela.
 *
 * ## Por que os cursos herdam as vagas dos cursos
 *
 * A alternativa — jogar os cursos para o começo e empurrar o resto — reordena a
 * faixa a cada clique num interruptor que não fala de ordem. Aqui os cursos
 * ocupam **as posições que já eram de curso**, na ordem recebida; o que sobra vai
 * para o fim. Módulo e aula não saem do lugar enquanto houver vaga, e ligar um
 * curso não mexe em cartão que ninguém tocou.
 */
export function trocarCursosDaPrateleira(
  atuais: ChaveDoItem[],
  cursos: string[],
): ChaveDoItem[] {
  const novos = cursos.map((id): ChaveDoItem => ({ tipo: 'curso', id }));

  const saida: ChaveDoItem[] = [];
  let proximo = 0;
  for (const item of atuais) {
    if (item.tipo !== 'curso') {
      saida.push(item);
      continue;
    }
    /* Vaga de curso: recebe o próximo da lista nova, ou desaparece se a lista
     * nova acabou — que é como "tirar um curso" acontece. */
    if (proximo < novos.length) saida.push(novos[proximo]!);
    proximo += 1;
  }
  /* Sobrou curso novo e não havia vaga: vai para o fim, que é onde quem
   * acrescenta espera encontrar o que acrescentou. */
  for (; proximo < novos.length; proximo += 1) saida.push(novos[proximo]!);

  return saida;
}

/**
 * A linha de baixo do cartão.
 *
 * ## Por que os três não cabem no mesmo molde
 *
 * "N aulas" num cartão de **aula** é sempre `1 aula`, que não informa nada. Um
 * molde só obrigaria os três a dizer a mesma coisa, e dois dos três mentiriam
 * por omissão.
 *
 * Então cada tipo diz o que ele sabe, e **`null` é uma resposta legítima**: uma
 * aula de texto não tem duração, e escrever `0 min` seria inventar. O cartão que
 * recebe `null` não desenha a linha.
 *
 * A contagem do curso não está aqui porque ela já é servida pela consulta da
 * vitrine (`aulas`, em `aluno.ts`) — repeti-la seria a segunda implementação. A
 * carga entra pela mesma porta, e pela mesma razão.
 *
 * ## ⚠ ESTE PARÁGRAFO DIZIA O CONTRÁRIO ATÉ 28/09/2026
 *
 * Estava escrito aqui que *«duração num cartão de curso […] diria `14 h 22 min`,
 * um número que ninguém usa para decidir se clica»*. O dono pediu exactamente
 * esse número, e a recusa antiga continua **verdadeira no seu caso**: somar o
 * curso inteiro do lado de cá, item a item, é que era o problema — a série de um
 * item só não tem o que somar, e somar a árvore no cliente devolveria zero (as
 * 44 aulas com vídeo e `duration_seconds` nulo, medidas em 23/09/2026).
 *
 * O que mudou não foi a opinião: foi quem faz a conta. A carga chega pronta, em
 * segundos, resolvida pelo servidor com `cargaDoCurso` — a herança curso↔área e
 * a precedência declarado↔medido já aplicadas. Aqui só se escolhe o separador.
 *
 * `null` continua a ser o estado normal: área que não ligou o recurso, curso que
 * o desligou, curso sem um segundo de vídeo. Nos três a linha volta a ser a
 * contagem de aulas sozinha, **byte a byte** como era antes desta obra.
 */
export function legendaDoItem(
  item: ItemDePrateleira,
  aulasDoCurso: number | null = null,
  cargaSegundos: number | null = null,
  /*
   * O tradutor (`A8`). Esta linha — «48 aulas · 10 h 42 min» — é o que o
   * aluno lê debaixo de cada cartão da vitrine, e ela nasce no SERVIDOR: a
   * varredura do JSX nunca a veria, e a do `core/area` não a via porque
   * este módulo não estava na lista dela. Foi o navegador que a mostrou.
   */
  t: Tradutor = tradutorDe(null),
): string | null {
  switch (item.tipo) {
    case 'curso': {
      if (aulasDoCurso === null || aulasDoCurso <= 0) return null;
      const aulas = t(aulasDoCurso === 1 ? 'item.umaAula' : 'item.aulas', { n: aulasDoCurso });
      const carga = duracao(cargaSegundos);
      return carga === null ? aulas : `${aulas} · ${carga}`;
    }
    case 'trilha': {
      /*
       * «4 cursos · 18 h» — e o substantivo é CURSO, não aula, porque é o que
       * distingue a trilha do curso ao lado dela na mesma fileira. A carga vem
       * pronta, em segundos, pelo mesmo `cargaSegundos` do curso: um segundo
       * formatador aqui diria `18 h` onde o cartaz do vizinho diz `18 h 40 min`.
       */
      const cursos = t(item.cursosVisiveis === 1 ? 'item.umCurso' : 'item.cursos', {
        n: item.cursosVisiveis,
      });
      const carga = duracao(cargaSegundos);
      return carga === null ? cursos : `${cursos} · ${carga}`;
    }
    case 'modulo':
      return t(item.aulasPublicadas === 1 ? 'item.umaAula' : 'item.aulas', {
        n: item.aulasPublicadas,
      });
    case 'aula':
      return duracao(item.duracaoSegundos);
  }
}

/*
 * `plural` ficou sem chamador quando a legenda passou pelo dicionário: as
 * duas formas são agora DUAS CHAVES, porque o plural não é «+s» em toda a
 * parte — em alemão «1 Lektion» e «2 Lektionen» mudam a palavra.
 */

/**
 * `12 min`, `1 h 05 min`, `48 s`.
 *
 * Segundos aparecem só abaixo de um minuto — acima disso `1 min` já é a leitura
 * que a pessoa faz, e `1 min 12 s` é precisão que ninguém pediu. Zero e nulo
 * caem no mesmo lugar: sem linha. Um vídeo de zero segundo é dado faltando, não
 * um vídeo instantâneo.
 */
export function duracao(segundos: number | null): string | null {
  if (segundos === null || !Number.isFinite(segundos) || segundos <= 0) return null;
  const s = Math.round(segundos);
  if (s < 60) return `${s} s`;
  const minutos = Math.round(s / 60);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas} h` : `${horas} h ${String(resto).padStart(2, '0')} min`;
}

/**
 * A primeira aula de um módulo na ordem de leitura do curso.
 *
 * `ordem` é a `ordemLinearDasAulas` do curso inteiro (sem módulo escondido);
 * `modulos` é o próprio módulo mais os submódulos dele. É para onde o cartão de
 * um módulo leva na vitrine — e `null` quando não há aula: o item some, em vez
 * de abrir uma lista vazia. Pura, para a cerca provar sem banco.
 */
export function primeiraAulaDoModulo(
  ordem: readonly string[],
  aulas: readonly { id: string; moduleId: string }[],
  modulos: ReadonlySet<string>,
): string | null {
  const moduloDa = new Map(aulas.map((a) => [a.id, a.moduleId]));
  for (const id of ordem) {
    const m = moduloDa.get(id);
    if (m !== undefined && modulos.has(m)) return id;
  }
  return null;
}

/**
 * Quantas aulas publicadas cada módulo PEDIDO tem: as próprias mais as dos
 * filhos diretos (S3). Um módulo raiz soma o subárvore de um nível — a legenda
 * «3 aulas» tem de contar o curso todo abaixo dele; um SUBMÓDULO pedido conta
 * as suas. Era agrupado por `coalesce(parent_id, id)` no SQL, e o submódulo
 * pedido caía na chave do pai: `contagem.get(sub)` dava 0 e o item sumia.
 */
export function aulasPorModulo(
  linhas: readonly { id: string; parentId: string | null; n: number }[],
  pedidos: readonly string[],
): Map<string, number> {
  const proprias = new Map(linhas.map((l) => [l.id, l.n]));
  const dosFilhos = new Map<string, number>();
  for (const l of linhas) {
    if (l.parentId) dosFilhos.set(l.parentId, (dosFilhos.get(l.parentId) ?? 0) + l.n);
  }
  const saida = new Map<string, number>();
  for (const id of pedidos) saida.set(id, (proprias.get(id) ?? 0) + (dosFilhos.get(id) ?? 0));
  return saida;
}
